<template>
  <div class="pb-wrap">
    <div class="card" style="width:min(700px,100%);">
      <h1 class="h1">Cámara</h1>
      <p class="p">Alinea tu rostro dentro del marco. La foto se captura en proporción 50×90mm.</p>

      <div class="cam-zone">
        <div class="frame" :class="{ mirrored: mirrorPreview }">
          <video
            v-show="!capturedDataUrl"
            ref="video"
            class="video"
            playsinline
            autoplay
            muted
          />

          <img
            v-if="capturedDataUrl"
            :src="capturedDataUrl"
            class="preview"
            alt="Foto capturada"
          />

          <div class="frame-guides"></div>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <div class="row" style="margin-top: 14px; justify-content: space-between;">
        <button class="btn secondary" @click="goBack">Volver</button>

        <div class="row" style="margin-left:auto;">
          <button class="btn secondary" :disabled="loading" @click="toggleCamera">
            Cambiar cámara
          </button>

          <button
            v-if="!capturedDataUrl"
            class="btn"
            :disabled="loading"
            @click="takePhoto"
          >
            {{ loading ? 'Iniciando…' : 'Tomar foto' }}
          </button>

          <button
            v-else
            class="btn secondary"
            @click="retake"
          >
            Retomar
          </button>

          <button
            v-if="capturedDataUrl"
            class="btn"
            @click="confirm"
          >
            Confirmar
          </button>
        </div>
      </div>

      <canvas ref="captureCanvas" class="hidden-canvas"></canvas>
    </div>
  </div>
</template>

<script>
import { DEFAULT_FACING_MODE, MIRROR_CAPTURE, MIRROR_PREVIEW, OUTPUT_ASPECT, OUTPUT_HEIGHT_PX, OUTPUT_WIDTH_PX } from '@/config';
import { getSession, requireFields, setSession } from '@/utils/session';

export default {
  name: 'CameraView',
  data() {
    return {
      stream: null,
      facingMode: DEFAULT_FACING_MODE,
      loading: false,
      error: '',
      capturedDataUrl: '',
      mirrorPreview: MIRROR_PREVIEW,
    };
  },
  mounted() {
    // Asegura que vengan del paso 1
    const { ok } = requireFields(['nombre', 'aceptaTerminos']);
    if (!ok) {
      this.$router.replace('/');
      return;
    }
    this.startCamera();
  },
  beforeUnmount() {
    this.stopCamera();
  },
  methods: {
    async startCamera() {
      this.error = '';
      this.loading = true;

      try {
        this.stopCamera();

        const constraints = {
          audio: false,
          video: {
            facingMode: this.facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.stream = stream;

        const video = this.$refs.video;
        video.srcObject = stream;

        await video.play();
      } catch (e) {
        console.error(e);
        this.error = 'No se pudo acceder a la cámara. Revisa permisos del navegador.';
      } finally {
        this.loading = false;
      }
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
    },

    toggleCamera() {
      // user ↔ environment
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      this.capturedDataUrl = '';
      this.startCamera();
    },

    takePhoto() {
      this.error = '';
      const video = this.$refs.video;
      const canvas = this.$refs.captureCanvas;
      if (!video?.videoWidth || !video?.videoHeight) {
        this.error = 'La cámara aún no está lista. Intenta de nuevo.';
        return;
      }

      // Calcula recorte centrado para mantener proporción 5:9
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const desiredRatio = OUTPUT_ASPECT;

      let sx = 0, sy = 0, sw = vw, sh = vh;
      const currentRatio = vw / vh;

      if (currentRatio > desiredRatio) {
        // Demasiado ancho → recorta ancho
        sw = vh * desiredRatio;
        sx = (vw - sw) / 2;
      } else {
        // Demasiado alto → recorta alto
        sh = vw / desiredRatio;
        sy = (vh - sh) / 2;
      }

      canvas.width = OUTPUT_WIDTH_PX;
      canvas.height = OUTPUT_HEIGHT_PX;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        this.error = 'No se pudo inicializar el canvas.';
        return;
      }

      // Si queremos que la captura salga igual que el preview (mirror)
      if (this.facingMode === 'user' && MIRROR_CAPTURE) {
        ctx.save();
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      }

      this.capturedDataUrl = canvas.toDataURL('image/png');
      // Pausa cámara para que no consuma mientras revisa
      this.stopCamera();
    },

    retake() {
      this.capturedDataUrl = '';
      this.startCamera();
    },

    confirm() {
      if (!this.capturedDataUrl) return;
      setSession({ fotoDataUrl: this.capturedDataUrl, fotoW: OUTPUT_WIDTH_PX, fotoH: OUTPUT_HEIGHT_PX });
      this.$router.push('/card');
    },

    goBack() {
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.cam-zone {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.frame {
  width: min(380px, 100%);
  aspect-ratio: 5 / 9;
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(0,0,0,0.25);
}

.frame.mirrored video {
  transform: scaleX(-1);
}

.video, .preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.frame-guides {
  position: absolute;
  inset: 0;
  border: 2px solid rgba(0,188,212,0.6);
  border-radius: 18px;
  pointer-events: none;
  box-shadow: inset 0 0 0 9999px rgba(0,0,0,0.08);
}

.hidden-canvas {
  display: none;
}
</style>
